import React, {useEffect, useRef, useState} from 'react';
import useDialogA11y from '../hooks/useDialogA11y';
import {ShieldIcon, XIcon} from './icons';

interface OtpDialogProps {
    /** Проверка кода. Должна бросать Error('INVALID_OTP' | 'MFA_EXPIRED') при ошибке. */
    onVerify: (code: string) => Promise<void>;
    onClose: () => void;
    title?: string;
    description?: string;
}

/**
 * Второй шаг входа: код из приложения-аутентификатора либо recovery-код.
 * Показывается только тем, у кого 2FA включена, — форма входа остаётся из двух полей.
 */
const OtpDialog: React.FC<OtpDialogProps> = ({onVerify, onClose, title, description}) => {
    const [code, setCode] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const dialogRef = useRef<HTMLDivElement>(null);

    // Escape, ловушка и возврат фокуса — общий хук (он же из этой модалки и вырос).
    useDialogA11y(dialogRef, onClose);

    // Фокус сразу в поле кода: вводить его — единственное, зачем окно открылось.
    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (loading) {
            return;
        }
        setLoading(true);
        setError('');
        try {
            await onVerify(code.trim());
        } catch (err: any) {
            setError(err?.message === 'MFA_EXPIRED'
                ? 'Время подтверждения истекло. Закройте окно и войдите заново.'
                : 'Неверный код. Проверьте код в приложении-аутентификаторе.');
            setLoading(false);
        }
    };

    return (
        <div className="fw-overlay fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose}/>
            <div
                ref={dialogRef}
                className="relative z-10 w-full max-w-sm card p-6"
                role="dialog"
                aria-modal="true"
                aria-label="Подтверждение входа"
            >
                <button
                    type="button"
                    onClick={onClose}
                    className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                    aria-label="Закрыть"
                >
                    <XIcon className="w-5 h-5"/>
                </button>

                <div className="w-12 h-12 rounded-2xl bg-ink-900 text-white dark:bg-white dark:text-ink-900 flex items-center justify-center mb-4">
                    <ShieldIcon className="w-6 h-6"/>
                </div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {title || 'Двухфакторная аутентификация'}
                </h2>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    {description || 'Введите код из приложения-аутентификатора или один из recovery-кодов.'}
                </p>

                <form onSubmit={submit} className="mt-5 space-y-4">
                    <input
                        ref={inputRef}
                        type="text"
                        maxLength={10}
                        autoComplete="one-time-code"
                        aria-label="Код подтверждения"
                        value={code}
                        onChange={(e) => setCode(e.target.value.replace(/[^a-zA-Z0-9-]/g, '').toUpperCase())}
                        placeholder="123456"
                        className="input-field text-center text-2xl tracking-[0.4em] font-mono"
                    />

                    {error && <p className="text-sm text-red-600 dark:text-red-400" role="alert">{error}</p>}

                    <button
                        type="submit"
                        disabled={loading || code.length < 6}
                        className="btn-primary w-full min-h-[44px]"
                    >
                        {loading ? 'Проверка...' : 'Подтвердить'}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default OtpDialog;
